import React, { FormEvent, useEffect, useState } from "react";
import { ISection } from "../../../../types/section";
import { IPage } from "../../../../types/page";
import useUpdatePage from "../../../../hooks/page/useUpdatePage";
import useCreatePage from "../../../../hooks/page/useCreatePage";
import useDeletePage from "../../../../hooks/page/useDeletePage";
import { ICriteriaGroup } from "../../../../types/criteriaGroup";

interface IPageProps {
  sections: ISection[];
  currOption: number[];
  setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
  isDelete: boolean;
  setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  criteriaGroups: ICriteriaGroup[];
}

const Page = ({
  sections,
  currOption,
  setCurrOption,
  isDelete,
  setIsDelete,
  setIsOpenDeleteModal,
  criteriaGroups,
}: IPageProps) => {
  //eslint-disable-line

  const secIdx = currOption[0] || 0;
  const sectionId = sections[secIdx].id;
  const pageId = currOption[1] || 0;
  const [page, setPage] = useState(sections[0].pages[0]);
  const [title, setTitle] = useState<string>("");
  const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
  const [isServiceError, setIsServiceError] = useState<string | null>(null); //eslint-disable-line

  const blankPage: IPage = {
    section_id: 1,
    criteria_group_id: undefined,
    order: 1,
    title: "",
    help: "",
    questions: [],
  };

  useEffect(() => {
    if (pageId === sections[secIdx].pages.length) {
      setPage({ ...blankPage });
      setTitle("New Page");
    } else {
      setPage(sections[secIdx].pages[pageId]);
      setTitle(sections[secIdx].pages[pageId].title);
    }
  }, [pageId, sections]); //eslint-disable-line

  const { mutate: updatePage } = useUpdatePage({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: createPage } = useCreatePage({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: deletePage } = useDeletePage({
    setIsProcessRunning,
    setIsServiceError,
  });

  useEffect(() => {
    if (isDelete) {
      setCurrOption([secIdx, 0]);
      if (page.id) deletePage(page.id);
      setIsDelete(false);
    }
  }, [isDelete]); //eslint-disable-line

  if (!page) {
    return <p>Loading...</p>;
  }

  const handleSave = (e: FormEvent) => {
    e.preventDefault();

    if (pageId === sections[secIdx].pages.length && sectionId) {
      createPage({
        sectionId,
        title: page.title,
        help: page.help,
        criteriaGroupId: page.criteria_group_id,
      });
    } else {
      updatePage({
        title: page.title,
        help: page.help,
        id: page.id,
        criteriaGroupId: page.criteria_group_id,
      });
    }
    setTitle(page.title);
  };

  const handleDelete = () => {
    if (pageId === sections[secIdx].pages.length) {
      if (!pageId) {
        setCurrOption([secIdx]);
      } else {
        setCurrOption([secIdx, 0]);
      }
      setPage(sections[secIdx].pages[pageId]);
    } else {
      setIsOpenDeleteModal(true);
    }
  };

  return (
    <div className="imm_quest_admin__content">
      <div className="content_head">
        <h3>{title}</h3>
      </div>

      <form onSubmit={handleSave} className="content_form">
        <div className="field">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            name="title"
            placeholder="Enter title"
            value={page.title}
            onChange={(e) => setPage({ ...page, title: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor="help">Help</label>
          <input
            id="help"
            name="help"
            placeholder="Enter help"
            value={page.help}
            onChange={(e) => setPage({ ...page, help: e.target.value })}
          />
        </div>

        {criteriaGroups && criteriaGroups.length > 0 && (
          <div className="field">
            <label htmlFor="criteriaGroup">Criteria Group</label>
            <select
              id="criteriaGroup"
              name="criteriaGroup"
              value={page.criteria_group_id || ""}
              onChange={(e) =>
                setPage({
                  ...page,
                  criteria_group_id: parseInt(e.target.value),
                })
              }
            >
              <option value={""}>-- Select criteria group --</option>
              {criteriaGroups.map((criteriaGroup) => (
                <option key={criteriaGroup.id} value={criteriaGroup.id}>
                  {criteriaGroup.title}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="actions">
          <button className="delete_btn" type="button" onClick={handleDelete}>
            {isProcessRunning ? (
              <i className="imm_quest_admin__process_loader" />
            ) : pageId === sections[secIdx].pages.length ? (
              "Cancel"
            ) : (
              "Delete"
            )}
          </button>
          <button className="save_btn" type="submit">
            {isProcessRunning ? (
              <i className="imm_quest_admin__process_loader" />
            ) : pageId === sections[secIdx].pages.length ? (
              "Create"
            ) : (
              "Save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Page;
